import React, { useEffect, useState } from "react";
import { Clock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * AdQueuePanel — manages preloaded ads + interval schedule per station.
 * Extracted from RadioHostStudio.
 */
export default function AdQueuePanel({ slug, tracks }) {
  const [ads, setAds] = useState([]);
  const [pickTrack, setPickTrack] = useState("");
  const [interval, setIntervalMin] = useState(15);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/radio/host/ad-queue/${slug}`);
      setAds(data || []);
    } catch {}
  };
  useEffect(() => { load(); }, [slug]);

  const add = async () => {
    if (!pickTrack) return;
    try {
      await api.post("/radio/host/ad-queue", { station_slug: slug, track_id: pickTrack, interval_min: interval });
      toast.success("Ad scheduled"); setPickTrack(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/radio/host/ad-queue/${id}`); toast.success("Ad removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="radio-host-ads" className="rounded-lg border border-white/10 bg-black/30">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <Clock className="w-3.5 h-3.5 text-tv-accent" />
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-[11px] uppercase tracking-wider">Ad Schedule</div>
          <div className="text-white/50 text-[10px]">{ads.length} scheduled · auto-rotates while live</div>
        </div>
        <span className="text-white/40 text-[10px]">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-center gap-2">
            <select
              data-testid="radio-host-ad-pick"
              value={pickTrack}
              onChange={(e) => setPickTrack(e.target.value)}
              className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-[11px]"
            >
              <option value="">Pick a library track…</option>
              {tracks.map((t) => <option key={t.id} value={t.id}>{`${t.title} — ${t.artist}`}</option>)}
            </select>
            <input
              data-testid="radio-host-ad-interval"
              type="number" min="1" max="240"
              value={interval}
              onChange={(e) => setIntervalMin(parseInt(e.target.value) || 15)}
              className="w-14 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-[11px]"
            />
            <span className="text-white/60 text-[10px]">min</span>
            <button
              data-testid="radio-host-ad-add"
              onClick={add}
              disabled={!pickTrack}
              className="bg-tv-accent text-black text-[10px] font-black px-2 py-1 rounded disabled:opacity-40"
            >
              <Plus className="w-3 h-3 inline" /> Add
            </button>
          </div>
          <div className="space-y-1" data-testid="radio-host-ad-list">
            {ads.length === 0 && <div className="text-white/40 text-[10px] text-center py-2">No ads scheduled yet.</div>}
            {ads.map((a) => (
              <div key={a.id} className="bg-white/5 rounded p-1.5 flex items-center gap-1.5">
                <div className="w-8 h-8 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${a.track?.cover_url})`, backgroundSize: "cover" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold line-clamp-1">{a.track?.title || "(missing track)"}</div>
                  <div className="text-white/50 text-[9px]">Every {a.interval_min} min · {a.plays_count || 0} plays</div>
                </div>
                <button onClick={() => del(a.id)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1 rounded">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
